import { useContext, useLayoutEffect, useRef } from 'react';
import { UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18n';

// Register before BrowserRouter mounts so a cancelled POP never unmounts the draft.
const popGuards = new Set();
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', event => {
    for (const guard of popGuards) guard(event);
  }, true);
}

// One router history entry per step, while form data remains in the mounted page.
export default function useCreationHistory() {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const id = useRef(location.state?.creation?.id || location.key);
  const dirty = useRef(false);
  const current = useRef(location);
  const index = useRef(window.history.state?.idx ?? 0);
  const restoring = useRef(false);
  current.current = location;
  index.current = window.history.state?.idx ?? index.current;
  const step = location.state?.creation?.id === id.current ? location.state.creation.step : 1;

  useLayoutEffect(() => {
    if (!location.state?.creation) {
      navigate({ pathname: location.pathname, search: location.search }, {
        replace: true, state: { ...location.state, creation: { id: id.current, step: 1 } },
      });
    }
  }, [location, navigate]);

  useLayoutEffect(() => {
    const confirmLeave = () => !dirty.current || window.confirm(i18n.t('stay.leaveDraft'));
    const push = navigator.push;
    const replace = navigator.replace;
    const guard = method => (to, state, options) => {
      if (state?.creation?.id === id.current || confirmLeave()) method.call(navigator, to, state, options);
    };
    navigator.push = guard(push);
    navigator.replace = guard(replace);
    const pop = event => {
      if (restoring.current) { restoring.current = false; return; }
      if (event.state?.usr?.creation?.id === id.current) return;
      if (!confirmLeave()) {
        event.stopImmediatePropagation();
        restoring.current = true;
        window.history.go(index.current - (event.state?.idx ?? index.current - 1));
      }
    };
    const unload = event => {
      if (dirty.current) { event.preventDefault(); event.returnValue = ''; }
    };
    popGuards.add(pop);
    window.addEventListener('beforeunload', unload);
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      popGuards.delete(pop);
      window.removeEventListener('beforeunload', unload);
    };
  }, [navigator]);

  const setStep = update => {
    const next = typeof update === 'function' ? update(step) : update;
    if (next === step) return;
    if (next === step - 1) { navigate(-1); return; }
    navigate({ pathname: current.current.pathname, search: current.current.search }, {
      state: { ...current.current.state, creation: { id: id.current, step: next } },
    });
  };
  return { step, setStep, markDirty: () => { dirty.current = true; }, markSaved: () => { dirty.current = false; } };
}
